/**
 * Drawer Component
 * 
 * A sliding drawer/panel component with backdrop overlay.
 * Slides in from the right (default) or left side.
 */

import { ReactNode, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

const slideInRight = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOutRight = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`;

const slideInLeft = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOutLeft = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-100%);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

interface BackdropProps {
  isClosing: boolean;
  backdropColor: string;
}

const Backdrop = styled.div<BackdropProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ backdropColor }) => backdropColor};
  z-index: 99998;
  animation: ${({ isClosing }) => (isClosing ? fadeOut : fadeIn)} 0.3s ease-out forwards;
`;

interface DrawerPanelProps {
  width: string;
  position: 'left' | 'right';
  isClosing: boolean;
  backgroundColor?: string;
}

const DrawerPanel = styled.div<DrawerPanelProps>`
  position: fixed;
  top: 0;
  ${({ position }) => (position === 'right' ? 'right: 0;' : 'left: 0;')}
  width: ${({ width }) => width};
  max-width: 100vw;
  height: 100vh;
  height: 100dvh;
  background-color: ${({ backgroundColor, theme }) => 
    backgroundColor || theme.colors?.background?.primary || '#ffffff'};
  z-index: 99999;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: ${({ position, theme }) =>
    position === 'right'
      ? theme.shadow?.md || '-4px 0 16px rgba(0, 0, 0, 0.1)'
      : theme.shadow?.md || '4px 0 16px rgba(0, 0, 0, 0.1)'};
  animation: ${({ position, isClosing }) => {
    if (position === 'right') {
      return isClosing ? slideOutRight : slideInRight;
    }
    return isClosing ? slideOutLeft : slideInLeft;
  }} 0.3s ease-out forwards;
`;

const DrawerContent = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export interface DrawerProps {
  isOpen: boolean;
  onClose?: () => void;
  onClickOutside?: () => void;
  position?: 'left' | 'right';
  width?: string;
  backdropColor?: string;
  backgroundColor?: string;
  children?: ReactNode;
}

export const Drawer = ({
  isOpen,
  onClose,
  onClickOutside,
  position = 'right',
  width = '470px',
  backdropColor = 'rgba(0, 0, 0, 0.5)',
  backgroundColor = '#ffffff',
  children,
}: DrawerProps) => {
  const isClosingRef = useRef(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Disable body scroll when drawer is open
  useEffect(() => {
    if (typeof window !== 'undefined' && window.document) {
      if (isOpen) {
        document.body.style.overflowY = 'hidden';
      } else {
        document.body.style.overflowY = 'unset';
      }
    }

    return () => {
      if (typeof window !== 'undefined' && window.document) {
        document.body.style.overflowY = 'unset';
      }
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && onClose) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = () => {
    if (onClickOutside) {
      onClickOutside();
    } else if (onClose) {
      onClose();
    }
  };

  return (
    <>
      <Backdrop
        isClosing={isClosingRef.current}
        backdropColor={backdropColor}
        onClick={handleBackdropClick}
        data-testid="drawer-backdrop"
      />
      <DrawerPanel
        ref={drawerRef}
        width={width}
        position={position}
        isClosing={isClosingRef.current}
        backgroundColor={backgroundColor}
        data-testid="drawer-panel"
      >
        <DrawerContent>{children}</DrawerContent>
      </DrawerPanel>
    </>
  );
};

export default Drawer;

/* ---------- composable sub-components ------------------------------------ */

export const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.space.md} 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
`;

export const DrawerTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const DrawerCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.space.xs};
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.space.md} 20px;
`;

export const DrawerFooter = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.md};
  padding: ${({ theme }) => theme.space.md} 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.color};
`;

export const DrawerPrimaryButton = styled.button`
  flex: 1;
  padding: ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  text-transform: uppercase;
  background: ${({ theme }) => theme.colors.pallete.secondary.color};
  color: ${({ theme }) => theme.colors.pallete.secondary.contrast};
  border: none;
  border-radius: ${({ theme }) => theme.radius.small};
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`;

export const DrawerOutlineButton = styled.button`
  flex: 1;
  padding: ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  text-transform: uppercase;
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 2px solid ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.small};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }
`;

